// src/random.ts
function random(lower, upper) {
  if (lower === void 0) {
    ;
    [lower, upper] = [0, 1];
  }
  if (upper === void 0) {
    ;
    [lower, upper] = [0, lower];
  }
  return Math.floor(Math.random() * (upper - lower + 1)) + lower;
}

// src/sample.ts
function sample(list = []) {
  const len = list.length;
  return len ? list[random(len - 1)] : void 0;
}

// src/sampleSize.ts
function sampleSize(list, n = 1) {
  if (n <= 0 || Number.isNaN(n) || !list?.length) {
    return [];
  }
  const result = [...list];
  const len = result.length;
  n = n > len ? len : n;
  for (let i = len - 1; i >= len - n; i--) {
    const rand = random(i);
    [result[i], result[rand]] = [result[rand], result[i]];
  }
  return result.slice(-n);
}

// src/shuffle.ts
function shuffle(list) {
  return sampleSize(list, list.length);
}

// src/uniq.ts
function uniq(list) {
  return [...new Set(list)];
}

// src/uniqBy.ts
function uniqBy(list, by) {
  const uniqMap = {};
  for (let item of list ?? []) {
    const key = by(item);
    if (!uniqMap[key]) {
      uniqMap[key] = item;
    }
  }
  return Object.values(uniqMap);
}

// src/omitBy.ts
function omitBy(obj, predicate = () => true) {
  if (!obj) {
    return {};
  }
  return Object.fromEntries(
    Object.entries(obj).filter(
      ([key, value]) => !Boolean(predicate(value, key))
    )
  );
}

// src/pickBy.ts
function pickBy(obj, predicate = (value) => Boolean(value)) {
  if (!obj) {
    return {};
  }
  return Object.fromEntries(
    Object.entries(obj).filter(([key, value]) => Boolean(predicate(value, key)))
  );
}

// src/pick.ts
function pick(obj, paths = []) {
  if (!obj) {
    return {};
  }
  return paths.reduce((acc, key) => {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      acc[key] = obj[key];
    }
    return acc;
  }, {});
}

// src/omit.ts
function omit(obj, paths = []) {
  if (!obj) {
    return {};
  }
  return paths.reduce(
    (acc, key) => {
      delete acc[key];
      return acc;
    },
    { ...obj }
  );
}

// src/groupBy.ts
function groupBy(list, by) {
  if (!list?.length) {
    return {};
  }
  return list.reduce(
    (acc, x) => {
      const key = String(by(x));
      if (acc[key]) {
        acc[key].push(x);
      } else {
        acc[key] = [x];
      }
      return acc;
    },
    {}
  );
}

// src/keyBy.ts
function keyBy(list, by) {
  if (!list?.length) {
    return {};
  }
  return list.reduce(
    (acc, x) => {
      const key = String(by(x));
      acc[key] = x;
      return acc;
    },
    {}
  );
}

// src/chunk.ts
function chunk(list, size = 1) {
  const l = [];
  for (let i = 0; i < list.length; i++) {
    const index = Math.floor(i / Math.floor(size));
    l[index] = l[index] || [];
    l[index].push(list[i]);
  }
  return l;
}

// src/get.ts
function get(obj, path, defaultValue) {
  const paths = Array.isArray(path) ? path : String(path).replace(/\[(\w+)\]/g, ".$1").replace(/\["(\w+)"\]/g, ".$1").replace(/\['(\w+)'\]/g, ".$1").split(".");
  let result = obj;
  for (const p of paths) {
    result = result?.[p];
    if (result === void 0) {
      return defaultValue;
    }
  }
  return result;
}

// src/words.ts
function words(str) {
  return str.replace(/[A-Z]+/g, (x) => `.${x.toLowerCase()}`).replace(/[^\w]+/g, ".").split(".").filter(Boolean);
}

// src/camelCase.ts
function camelCase(str) {
  return words(str).map((word, i) => i ? word[0].toUpperCase() + word.slice(1) : word).join("");
}

// src/snakeCase.ts
function snakeCase(str) {
  return words(str).join("_");
}

// src/kebabCase.ts
function kebabCase(str) {
  return words(str).join("-");
}

// src/defaults.ts
function defaults(obj, ...sources) {
  return sources.reduce((obj2, source) => {
    for (const key of Object.keys(source)) {
      if (obj2[key] === void 0) {
        obj2[key] = source[key];
      }
    }
    return obj2;
  }, obj);
}

// src/isObject.ts
function isObject(value) {
  return (typeof value === "object" || typeof value === "function") && value !== null;
}

// src/isPlainObject.ts
function isPlainObject(value) {
  if (!isObject(value)) {
    return false;
  }
  if (Object.getPrototypeOf(value) === null) {
    return true;
  }
  let proto = value;
  while (Object.getPrototypeOf(proto) !== null) {
    proto = Object.getPrototypeOf(proto);
  }
  return Object.getPrototypeOf(value) === proto;
}

// src/isArray.ts
function isArray(value) {
  return Array.isArray(value);
}

// src/isPrimitive.ts
function isPrimitive(value) {
  return !isObject(value);
}

// src/clone.ts
function clone(obj) {
  if (isPrimitive(obj)) {
    return obj;
  }
  if (isArray(obj)) {
    return [...obj];
  }
  if (isPlainObject(obj)) {
    return {
      ...obj
    };
  }
  return obj;
}
function cloneDeep(obj) {
  if (!isArray(obj) && !isPlainObject(obj)) {
    return clone(obj);
  }
  if (isArray(obj)) {
    return obj.map((x) => cloneDeep(x));
  }
  return Object.keys(obj).reduce((acc, key) => {
    let value = obj[key];
    if (!isPrimitive(value)) {
      value = cloneDeep(value);
    }
    return {
      ...acc,
      [key]: value
    };
  }, {});
}

// src/merge.ts
var isArrayOrPlainObject = (o) => isPlainObject(o) || isArray(o);
function merge(object, ...sources) {
  if (sources.length === 1 && (!isArrayOrPlainObject(sources[0]) || !isArrayOrPlainObject(object))) {
    if (sources[0] === void 0) {
      return cloneDeep(object);
    }
    return cloneDeep(sources[0]);
  }
  for (const source of sources) {
    if (!isArrayOrPlainObject(source)) {
      object = merge(object, source);
    } else {
      for (const key of Object.keys(source)) {
        ;
        object[key] = merge(object[key], source[key]);
      }
    }
  }
  return object;
}

// src/isBoolean.ts
function isBoolean(value) {
  return value === false || value === true;
}

// src/isPromise.ts
function isPromise(value) {
  return isObject(value) && typeof value.then === "function";
}

// src/isTypedArray.ts
function isTypedArray(value) {
  return value.length !== void 0 && ArrayBuffer.isView(value);
}

// src/isEqual.ts
function isEqual(value, other) {
  if (value === other) {
    return true;
  }
  if (!(value && other && typeof value === "object" && typeof other === "object")) {
    return value !== other && other !== other;
  }
  if (value.constructor !== other.constructor) {
    return false;
  }
  if (Array.isArray(value)) {
    if (value.length !== other.length) {
      return false;
    }
    return value.every((x, i) => {
      return isEqual(x, other[i]);
    });
  }
  if (isTypedArray(value)) {
    if (value.length !== other.length) {
      return false;
    }
    return value.every((x, i) => x === other[i]);
  }
  if (value.constructor === RegExp) {
    return value.source === other.source && value.flags === other.flags;
  }
  if (value instanceof Map) {
    if (value.size !== other.size) {
      return false;
    }
    for (const x of value.keys()) {
      if (!other.has(x)) {
        return false;
      }
    }
    for (const [k, v] of Object.entries(value)) {
      if (!isEqual(v, other.get(k))) {
        return false;
      }
    }
    return true;
  }
  if (value instanceof Set) {
    if (value.size !== other.size) {
      return false;
    }
    for (const x of value.keys()) {
      if (!other.has(x)) {
        return false;
      }
    }
    return true;
  }
  const keys = Object.keys(value);
  const otherKeys = Object.keys(other);
  if (keys.length !== otherKeys.length) {
    return false;
  }
  for (const key of keys) {
    if (!Object.prototype.hasOwnProperty.call(other, key)) {
      return false;
    }
  }
  for (const key of keys) {
    if (!isEqual(value[key], other[key])) {
      return false;
    }
  }
  return true;
}

// src/range.ts
function range(start, end, step) {
  if (end === void 0) {
    end = start;
    start = 0;
  }
  if (step === 0) {
    return Array(Math.ceil(end - start)).fill(start);
  }
  step = step || (start > end ? -1 : 1);
  const r = [];
  for (let i = start; start > end ? i > end : i < end; i += step) {
    r.push(i);
  }
  return r;
}

// src/sum.ts
function sum(list) {
  return (list || []).reduce((x, y) => x + y, 0);
}

// src/max.ts
function max(list, by = (x) => x) {
  if (!list || !list.length) {
    return;
  }
  return list.reduce((x, y) => by(x) > by(y) ? x : y);
}

// src/min.ts
function min(list, by = (x) => x) {
  if (!list || !list.length) {
    return;
  }
  return list.reduce((x, y) => by(x) < by(y) ? x : y);
}

// src/compose.ts
function compose(...funcs) {
  return funcs.reduce(
    (a, b) => (...args) => a(b(...args)),
    (x) => x
  );
}

// src/difference.ts
var difference = (list, values = [], by = (x) => x) => {
  if (!list || !list.length)
    return [];
  if (!values.length)
    return [...list];
  const map2 = values.reduce(
    (acc, item) => ({
      ...acc,
      [by(item)]: true
    }),
    {}
  );
  return list.filter((a) => !map2[by(a)]);
};

// src/async.ts
function sleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}
var AbortError = class extends Error {
  constructor(message) {
    super();
    if (message instanceof Error) {
      this.originalError = message;
      ({ message } = message);
    } else {
      this.originalError = new Error(message);
      this.originalError.stack = this.stack;
    }
    this.name = "AbortError";
    this.message = message;
  }
};
async function retry(run, { times = 10, onFailedAttempt = () => {
} } = {}) {
  let count = 1;
  async function exec() {
    try {
      const result = await run(count);
      return result;
    } catch (e) {
      if (count >= times || e instanceof AbortError) {
        throw e;
      }
      count++;
      await onFailedAttempt(e);
      return exec();
    }
  }
  return exec();
}
function map(it, mapper, { concurrency = Infinity } = {}) {
  const list = Array.from(it);
  return new Promise((resolve) => {
    let currentIndex = 0;
    let result = [];
    let resolveCount = 0;
    let len = list.length;
    function next() {
      const index = currentIndex;
      currentIndex++;
      Promise.resolve(list[index]).then((o) => mapper(o, index)).then((o) => {
        result[index] = o;
        resolveCount++;
        if (resolveCount === len) {
          resolve(result);
        }
        if (currentIndex < len) {
          next();
        }
      });
    }
    for (let i = 0; i < concurrency && i < len; i++) {
      next();
    }
  });
}
async function filter(it, filterer, options) {
  const list = await map(
    it,
    async (item, index) => {
      const bool = await filterer(item, index);
      return [item, bool];
    },
    options
  );
  return list.filter(([_, bool]) => bool).map(([item]) => item);
}

// src/nth.ts
function nth(list, n = 0) {
  return n >= 0 ? list[n] : list[list.length + n];
}

// src/intersection.ts
function intersection(...args) {
  if (!args || !args.length)
    return [];
  if (args.slice(0, args.length - 1).find((item) => typeof item === "function"))
    return [];
  const list = args.filter(Array.isArray);
  const by = typeof args[args.length - 1] === "function" ? args[args.length - 1] : (x) => x;
  return list?.reduce((a, b) => {
    const setB = new Set(b.map(by));
    return a.filter((c) => setB.has(by(c)));
  });
}

// src/debounce.ts
function debounce(f, wait) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      f(...args);
    }, wait);
  };
}

// src/head.ts
function head(list) {
  return list?.[0];
}
function tail(list) {
  return list?.slice(1) ?? [];
}
function take(list, n = 1) {
  return list?.slice(0, n) ?? [];
}

// src/compact.ts
var compact = (arr) => {
  return arr.filter((item) => !!item);
};

// src/zip.ts
function zip(...arrays) {
  const maxLength = Math.max(...arrays.map((arr) => arr.length));
  return Array.from({ length: maxLength }, (_, i) => arrays.map((arr) => arr[i]));
}

// src/unzip.ts
function unzip(data) {
  const maxLength = Math.max(...data.map((row) => row.length));
  return Array.from({ length: maxLength }, (_, i) => data.map((row) => row[i]));
}

// src/assign.ts
function assign(target, ...sources) {
  if (target === null || target === void 0) {
    target = {};
  }
  return Object.assign(target, ...sources);
}

// src/mapKeys.ts
function mapKeys(obj, iteratee = (value) => value) {
  return Object.entries(obj).reduce(
    (acc, [key, value]) => {
      return {
        ...acc,
        [iteratee(value, key)]: value
      };
    },
    {}
  );
}
function mapValues(obj, iteratee = (value) => value) {
  return Object.entries(obj).reduce(
    (acc, [key, value]) => {
      return {
        ...acc,
        [key]: iteratee(value, key)
      };
    },
    {}
  );
}

// src/castArray.ts
function castArray(...args) {
  if (!args.length) {
    return [];
  }
  const value = args[0];
  return Array.isArray(value) ? value : [value];
}

// src/throttle.ts
function throttle(f, wait) {
  let timer;
  return (...args) => {
    if (timer) {
      return;
    }
    timer = setTimeout(() => {
      f(...args);
      timer = null;
    }, wait);
  };
}

// src/property.ts
var property = (path) => (obj) => get(obj, path);

// src/once.ts
function once(fn) {
  let called = false;
  return (...args) => {
    if (!called) {
      called = true;
      return fn(...args);
    }
  };
}

// src/memoize.ts
function memoize(fn) {
  let cache = {};
  return (...args) => {
    let key = args[0];
    if (cache[key] !== void 0) {
      return cache[key];
    } else {
      let result = fn(...args);
      cache[key] = result;
      return result;
    }
  };
}

// src/template.ts
var template = (str, data, regex = /\{\{(.+?)\}\}/g) => {
  return Array.from(str.matchAll(regex)).reduce((acc, match) => {
    return acc.replace(match[0], data[match[1].trim()]);
  }, str);
};
export {
  AbortError,
  assign,
  nth as at,
  camelCase,
  castArray,
  chunk,
  clone,
  cloneDeep,
  compact,
  compose,
  debounce,
  defaults,
  difference,
  difference as differenceBy,
  filter,
  compose as flowRight,
  get,
  groupBy,
  head,
  intersection,
  intersection as intersectionBy,
  isArray,
  isBoolean,
  isEqual,
  isObject,
  isPlainObject,
  isPrimitive,
  isPromise,
  isTypedArray,
  kebabCase,
  keyBy,
  map,
  mapKeys,
  mapValues,
  max,
  max as maxBy,
  memoize,
  merge,
  min,
  min as minBy,
  nth,
  omit,
  omitBy,
  once,
  pick,
  pickBy,
  property,
  random,
  range,
  retry,
  sample,
  sampleSize,
  shuffle,
  sleep,
  snakeCase,
  sum,
  tail,
  take,
  template,
  throttle,
  uniq,
  uniqBy,
  unzip,
  zip
};
//# sourceMappingURL=index.esm.js.map